if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.viewport = this.querySelector('.product-media-modal__viewport');
        this.track = this.querySelector('[data-product-modal-track]');
        this.currentNode = this.querySelector('[data-product-modal-current]');
        this.prevButton = this.querySelector('[data-product-modal-prev]');
        this.nextButton = this.querySelector('[data-product-modal-next]');
        this.onViewportScroll = this.onViewportScroll.bind(this);
        this.onViewportWheel = this.onViewportWheel.bind(this);
        this.onArrowNavigate = this.onArrowNavigate.bind(this);
        this.addEventListener('transitionend', (event) => {
          if (event.target === this) this.focusDialog();
        });

        this.prevButton?.addEventListener('click', () => this.navigate(-1, { behavior: 'auto' }));
        this.nextButton?.addEventListener('click', () => this.navigate(1, { behavior: 'auto' }));
        this.viewport?.addEventListener('scroll', this.onViewportScroll, { passive: true });
        this.viewport?.addEventListener('wheel', this.onViewportWheel, { passive: false });
      }

      hide() {
        window.clearTimeout(this.scrollTimer);
        document.removeEventListener('keydown', this.onArrowNavigate);
        super.hide();
      }

      show(opener) {
        super.show(opener);
        window.requestAnimationFrame(() => this.focusDialog());
        document.addEventListener('keydown', this.onArrowNavigate);
        this.showActiveMedia();
      }

      focusDialog() {
        if (this.hasAttribute('open') && !this.contains(document.activeElement)) {
          this.querySelector('[role="dialog"]')?.focus({ preventScroll: true });
        }
      }

      showActiveMedia() {
        const openedMediaId = this.openedBy?.getAttribute('data-media-id');
        const nextIndex = this.getSlideIndexByMediaId(openedMediaId);
        this.setActiveSlide(nextIndex, 'auto');
      }

      getSlides() {
        return Array.from(this.querySelectorAll('.product-media-modal__slide'));
      }

      getActiveIndex() {
        const slides = this.getSlides();
        if (!slides.length || !this.viewport) return 0;

        const viewportCenter = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
          const distance = Math.abs(slideCenter - viewportCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      getSlideIndexByMediaId(mediaId) {
        const slides = this.getSlides();
        const requestedIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        return requestedIndex >= 0 ? requestedIndex : 0;
      }

      normalizeIndex(index) {
        const slides = this.getSlides();
        if (!slides.length) return 0;

        return ((index % slides.length) + slides.length) % slides.length;
      }

      setActiveSlide(index, behavior = 'auto') {
        const slides = this.getSlides();
        if (!slides.length) return;

        const nextIndex = this.normalizeIndex(index);
        const nextSlide = slides[nextIndex];
        if (!nextSlide || !this.viewport) return;

        this.viewport.scrollTo({
          left: nextSlide.offsetLeft,
          behavior,
        });

        this.updateState(nextIndex);
        this.loadActiveMedia(nextSlide);
      }

      updateState(activeIndex = this.getActiveIndex()) {
        const slides = this.getSlides();
        if (!slides.length) return;

        slides.forEach((slide, index) => {
          slide.classList.toggle('is-active', index === activeIndex);
        });

        if (this.currentNode) {
          this.currentNode.textContent = `${activeIndex + 1}`;
        }
      }

      navigate(direction, options = {}) {
        this.setActiveSlide(this.getActiveIndex() + direction, options.behavior || 'auto');
      }

      onViewportScroll() {
        if (!this.hasAttribute('open')) return;
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => {
          const activeIndex = this.getActiveIndex();
          this.updateState(activeIndex);
          const activeSlide = this.getSlides()[activeIndex];
          if (activeSlide) {
            this.loadActiveMedia(activeSlide);
          }
        }, 60);
      }

      onViewportWheel(event) {
        if (!this.viewport) return;
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
        if (event.ctrlKey) return;

        const slides = this.getSlides();
        const activeIndex = this.getActiveIndex();
        if (event.deltaY > 0 && activeIndex === slides.length - 1) {
          event.preventDefault();
          this.setActiveSlide(0, 'auto');
          return;
        }

        if (event.deltaY < 0 && activeIndex === 0) {
          event.preventDefault();
          this.setActiveSlide(slides.length - 1, 'auto');
          return;
        }

        event.preventDefault();
        this.viewport.scrollLeft += event.deltaY;
      }

      onArrowNavigate(event) {
        if (!this.hasAttribute('open')) return;

        if (event.code === 'Escape') {
          event.preventDefault();
          this.hide();
          return;
        }

        if (event.code === 'Tab' && !this.contains(document.activeElement)) {
          event.preventDefault();
          this.focusDialog();
          return;
        }

        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.navigate(-1, { behavior: 'auto' });
        }

        if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.navigate(1, { behavior: 'auto' });
        }
      }

      loadActiveMedia(activeSlide) {
        if (!activeSlide) return;

        const template = activeSlide.querySelector('template[data-product-modal-image]');
        if (template) {
          const image = template.content.querySelector('img');
          if (image) image.loading = 'eager';
          template.replaceWith(template.content.cloneNode(true));
        }

        window.pauseAllMedia();
        const deferredMedia = activeSlide.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        }
      }
    }
  );
}
